import React, { useCallback, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Link as GatsbyLink } from 'gatsby';
import SwiperClass from 'swiper';
import { EffectFade } from 'swiper/modules';

import { Grid2 as Grid, Link, LinkProps, styled } from '@mui/material';

import { LinkData, StrapiMarketLanguageConfig, SubMenu } from '@mewa/types';
import {
  ConversionButton,
  ExternalLinkIcon,
  LoginIcon,
  MobileMenuBackIcon,
  MobileSubmenuIcon,
  ResponsiveGrid,
} from '@mewa/ui';

import t from '../../../../libs/translate';
import { useConversionButtonLabel } from '../../../a-b-testing/cta-label-experiment';
import { trackCustomEvent } from '../../../helpers/tracking-helper';
import { HeaderAndFooterTranslations, NavigationData } from '../types';
import classes from './menu.module.scss';

type Props = {
  consultation: { onClick: () => void; slug: string };
  langPrefix: string;
  translations: HeaderAndFooterTranslations;
  companyMenu: SubMenu;
  navigationLinks: NavigationData['navigationLinks'];
  marketLanguageConfig: StrapiMarketLanguageConfig;
  featureDev?: boolean;
  productMenu: SubMenu[];
};

enum MENU_SLIDES {
  ROOT,
  PRODUCT_INDUSTRY_OVERVIEW,
  PRODUCT_INDUSTRY_LINKS,
  COMPANY,
}

const MobileMenu = ({
  consultation,
  langPrefix,
  companyMenu: companyLinks,
  translations,
  navigationLinks,
  productMenu,
  marketLanguageConfig,
  featureDev,
}: Props) => {
  const [navSwiper, setNavSwiper] = useState<SwiperClass | null>(null);
  const { ctaLabel } = useConversionButtonLabel({ translations });

  const [productNavigationSlideIndex, setProductNavigationSlideIndex] =
    useState<number>(0);

  const onBack = useCallback(() => {
    navSwiper?.slidePrev();
  }, [navSwiper]);

  const labelBack = t(translations, 'general_form_button_label_back');

  const {
    navigation: { company, productMenu: productOverview },
    fullService,
  } = navigationLinks;

  const trackCustomerPortal = () => {
    trackCustomEvent('external_customerportal_header');
  };

  return (
    <Swiper
      slidesPerView={1}
      allowTouchMove={false}
      onSwiper={setNavSwiper}
      modules={[EffectFade]}
      effect="fade"
      className={classes.menuContainer}
    >
      <SwiperSlide className={classes.menuSlide}>
        <ResponsiveGrid>
          <Grid size={12}>
            <LinkList>
              <LinkItem>
                <MenuLink
                  onClick={() => {
                    navSwiper?.slideTo(MENU_SLIDES.PRODUCT_INDUSTRY_OVERVIEW);
                  }}
                >
                  {productOverview.name}
                </MenuLink>
              </LinkItem>
              <LinkItem>
                <NavigationLink to={`${langPrefix}/${fullService.slug}`}>
                  {fullService.name}
                </NavigationLink>
              </LinkItem>
              <LinkItem>
                <MenuLink
                  onClick={() => {
                    navSwiper?.slideTo(MENU_SLIDES.COMPANY);
                  }}
                >
                  {company.name}
                </MenuLink>
              </LinkItem>
            </LinkList>

            <LinkList>
              <LinkItem sx={{ marginTop: '30px' }}>
                <ExternalLink
                  withoutIcon
                  href={marketLanguageConfig.customerPortal}
                  onClick={trackCustomerPortal}
                  sx={{ display: 'inline-flex' }}
                >
                  <LoginIcon style={{ marginRight: '14px' }} />
                  {t(translations, 'general_login')}
                </ExternalLink>
              </LinkItem>
              <LinkItem>
                <ConversionButton
                  sx={{
                    marginTop: 0,
                    display: { sm: 'inline-block' },
                    marginRight: { md: 2 },
                  }}
                  link={{
                    component: GatsbyLink,
                    props: { to: consultation.slug },
                  }}
                  onClick={consultation.onClick}
                  variant="contained"
                >
                  {ctaLabel}
                </ConversionButton>
              </LinkItem>
            </LinkList>
          </Grid>
        </ResponsiveGrid>
      </SwiperSlide>
      <SwiperSlide className={classes.menuSlide}>
        <ProductMenuSlide
          langPrefix={langPrefix}
          onBack={onBack}
          labelBack={labelBack}
          onChange={(slideIndex) => {
            setProductNavigationSlideIndex(slideIndex);
            navSwiper?.slideTo(MENU_SLIDES.PRODUCT_INDUSTRY_LINKS);
          }}
          productOverview={productOverview}
          productMenu={productMenu}
        />
      </SwiperSlide>
      <SwiperSlide className={classes.menuSlide}>
        <LinkSlide
          langPrefix={langPrefix}
          links={productMenu[productNavigationSlideIndex]}
          onBack={onBack}
          labelBack={labelBack}
        />
      </SwiperSlide>
      <SwiperSlide className={classes.menuSlide}>
        <LinkSlide
          langPrefix={langPrefix}
          links={companyLinks}
          onBack={() => navSwiper?.slideTo(MENU_SLIDES.ROOT)}
          labelBack={labelBack}
        />
      </SwiperSlide>
    </Swiper>
  );
};

export default MobileMenu;

const ProductMenuSlide = ({
  langPrefix,
  onChange,
  onBack,
  productOverview,
  labelBack,
  productMenu,
}: {
  langPrefix: string;
  onChange: (slideIndex: number) => void;
  onBack: () => void;
  productOverview: LinkData;
  labelBack: string;
  productMenu: SubMenu[];
}) => (
  <ResponsiveGrid>
    <Grid size={12}>
      <LinkList>
        <LinkItem>
          <MenuBack onClick={() => onBack()}>{labelBack}</MenuBack>
        </LinkItem>
        <LinkItem>
          <NavigationLink
            to={`${langPrefix}/${productOverview.slug}`}
            isOverview
          >
            {productOverview.name}
          </NavigationLink>
        </LinkItem>
        {productMenu.map((category, index) => (
          <LinkItem key={category.slug}>
            {category.links.length === 0 ? (
              <NavigationLink to={`${langPrefix}/${category.slug}`}>
                {category.title}
              </NavigationLink>
            ) : (
              <MenuLink onClick={() => onChange(index)}>
                {category.title}
              </MenuLink>
            )}
          </LinkItem>
        ))}
      </LinkList>
    </Grid>
  </ResponsiveGrid>
);

const LinkSlide = ({
  onBack,
  labelBack,
  links,
  langPrefix,
}: {
  onBack: () => void;
  labelBack: string;
  links: SubMenu;
  langPrefix: string;
}) => (
  <ResponsiveGrid>
    <Grid size={12}>
      <LinkList>
        <LinkItem>
          <MenuBack onClick={() => onBack()}>{labelBack}</MenuBack>
        </LinkItem>
        <LinkSlideItem
          name={links.title}
          slug={links.slug}
          isOverview
          langPrefix={langPrefix}
        />
        {links.links.map((l) => (
          <LinkSlideItem key={l.slug} {...l} langPrefix={langPrefix} />
        ))}
      </LinkList>
    </Grid>
  </ResponsiveGrid>
);

const LinkSlideItem = ({
  name,
  slug,
  isExternal,
  isOverview = false,
  langPrefix,
}: {
  name: string;
  slug: string;
  isExternal?: boolean;
  isOverview?: boolean;
  langPrefix: string;
}) => (
  <LinkItem key={slug}>
    {isExternal ? (
      <ExternalLink href={slug}>{name}</ExternalLink>
    ) : (
      <NavigationLink to={`${langPrefix}/${slug}`} isOverview={isOverview}>
        {name}
      </NavigationLink>
    )}
  </LinkItem>
);
const ExternalLink = ({
  children,
  withoutIcon = false,
  ...props
}: React.PropsWithChildren<LinkProps & { withoutIcon?: boolean }>) => (
  <StyledLink
    {...props}
    target="_blank"
    rel="noreferrer"
    underline="none"
    color="secondary"
  >
    {children}
    {!withoutIcon ? (
      <ExternalLinkIcon
        style={{
          marginLeft: '5px',
          width: '25px',
          height: '25px',
          verticalAlign: 'middle',
        }}
      />
    ) : null}
  </StyledLink>
);

// For navigation links to dedicated pages
const NavigationLink = ({
  children,
  to,
  isOverview = false,
}: React.PropsWithChildren<{ to: string; isOverview?: boolean }>) => (
  <StyledLink
    component={GatsbyLink}
    to={to}
    underline="none"
    color="secondary"
    className={isOverview ? 'overview-link' : ''}
  >
    {children}
  </StyledLink>
);

// For links to nested menus
const MenuLink = ({
  children,
  ...props
}: React.PropsWithChildren<LinkProps>) => (
  <MenuLinkContainer {...props} underline="none" color="secondary">
    {children}
    <MobileSubmenuIcon style={{ marginRight: '10px' }} />
  </MenuLinkContainer>
);

const StyledLink = styled(Link)(({ theme }) => ({
  ...theme.typography.footnote,
  paddingTop: '10px',
  paddingBottom: '10px',
  display: 'block',
  '&.overview-link': {
    fontWeight: theme.typography.fontWeightMedium,
  },
})) as typeof Link;

const MenuLinkContainer = styled(StyledLink)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}) as typeof Link;

const MenuBack = ({
  children,
  ...props
}: React.PropsWithChildren<LinkProps>) => (
  <StyledBackButton {...props} underline="none" color="secondary">
    <MobileMenuBackIcon style={{ marginRight: '7px' }} />
    {children}
  </StyledBackButton>
);

const LinkList = styled('ul')({
  margin: 0,
  padding: 0,
  listStyle: 'none',
});

const LinkItem = styled('li')({
  marginTop: '4px',
  marginBottom: '4px',
});

const StyledBackButton = styled(Link)(({ theme }) => ({
  ...theme.typography.footnote,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  marginBottom: '6px',
  padding: '10px 0 10px',
}));
